import React, { useMemo, useCallback, useState } from 'react';
import styled from 'styled-components';
import {
  Globe,
  FileText,
  MessageCircle,
  Mail,
  Eye,
  Copy,
  Download,
  Loader2,
  CreditCard
} from 'lucide-react';
import { toast } from 'react-hot-toast';

// Import types and services
import { ContentGridProps, ContentI, ContentFile } from './types';
import { Logger } from '../../utils/logger';
import { shareContentLead } from '../../services/leadService';
import { downloadContentFile, contentBlobToFile } from '../../services/contentService';

// Enhanced Styled Components with Gilroy font and improved design
const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  width: 100%;
  padding: 24px;
  font-family: 'Gilroy', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  cursor: pointer;
  border: 1px solid rgba(229, 234, 240, 0.6);
  height: 100%;
  position: relative;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    border-color: rgba(63, 174, 251, 0.3);
  }

  &:active {
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid rgba(241, 245, 249, 0.8);
  display: flex;
  align-items: center;
  gap: 14px;
  background: linear-gradient(135deg, #fafbff 0%, #f8fafc 100%);
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%);
  color: #3B82F6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
`;

const CardTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  line-height: 1.4;
  flex-grow: 1;
  font-family: 'Gilroy', sans-serif;
  letter-spacing: -0.01em;
`;

const CardBody = styled.div`
  padding: 20px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const CardDescription = styled.p`
  margin: 0 0 20px 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.6;
  flex-grow: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-family: 'Gilroy', sans-serif;
`;

const CardFooter = styled.div`
  padding: 16px 20px 20px 20px;
  border-top: 1px solid rgba(241, 245, 249, 0.8);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(248, 250, 252, 0.5);
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto;
`;

const ContentTag = styled.span`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%);
  color: #1d4ed8;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: 'Gilroy', sans-serif;
  letter-spacing: 0.02em;
  border: 1px solid rgba(59, 130, 246, 0.1);
`;

const ViewCount = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #64748b;
  font-size: 13px;
  font-family: 'Gilroy', sans-serif;
  font-weight: 500;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: 'Gilroy', sans-serif;
  position: relative;
  overflow: hidden;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
    color: white;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.25);
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.35);
    }
  ` : `
    background: rgba(248, 250, 252, 0.8);
    color: #64748b;
    border: 1px solid rgba(226, 232, 240, 0.8);
    
    &:hover {
      background: rgba(241, 245, 249, 0.9);
      color: #475569;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }
  `}

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const LoadingSpinner = styled(Loader2)`
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// Type for processed item to improve type safety
interface ProcessedContentItem extends ContentI {
  truncatedTitle: string;
  truncatedTag: string;
  truncatedDescription: string;
}

// Icon mapping for different content types
const getContentTypeIcon = (activeTab: string) => {
  const iconMap = {
    'message': MessageCircle,
    'page': Globe,
    'file': FileText,
    'email_template': Mail,
    'digital_card': CreditCard,
    'default': FileText
  };

  return iconMap[activeTab as keyof typeof iconMap] || iconMap['default'];
};

const ContentGrid: React.FC<ContentGridProps> = React.memo(({
  filterData,
  activeTab,
  searchQuery,
  setMessageData,
  setActiveId,
  setOpenMessageBox,
  setOpenPageBox,
  setOpenFileBox,
  setOpenEmailBox,
  setOpenDigitalCardBox,
  setEmailData,
  setPageData,
  setDigitalCardData
}) => {
  // State for button loading states
  const [copyingStates, setCopyingStates] = useState<Record<string, boolean>>({});
  const [downloadingStates, setDownloadingStates] = useState<Record<string, boolean>>({});

  // Dynamic icon based on active tab
  const ContentTypeIcon = getContentTypeIcon(activeTab);

  // Memoized text truncation to prevent unnecessary recalculations
  const truncateText = useCallback((text: string, maxLength: number) => {
    if (!text) return '';
    return text.length <= maxLength 
      ? text 
      : `${text.slice(0, maxLength)}...`;
  }, []);

  // Copy functionality for different content types
  const handleCopyContent = useCallback(async (item: ContentI) => {
    const itemId = item._id;
    setCopyingStates(prev => ({ ...prev, [itemId]: true }));

    try {
      let textToCopy = '';

      switch (item.type) {
        case 'message':
          // Copy raw message text with @lead name placeholder
          textToCopy = item.details?.message || '';
          break;

        case 'page':
        case 'file':
          // Generate shareable link with precomposed message (like bulk share)
          try {
            Logger.log(`Generating shareable link for ${item.type}:`, item._id);
            
            const body = {
              contentId: item._id,
              leadIds: [], // Empty array for general sharing
              performedAt: new Date(),
              contentType: item.type,
            };
            
            const response = await shareContentLead(body);
            
            if (!response?.data?.data?.uniqueLink) {
              throw new Error('No unique link generated');
            }
            
            // Create precomposed WhatsApp message
            textToCopy = `Hi there,

Here's the link to view:
${response.data.data.uniqueLink}`;

          } catch (error) {
            Logger.log('Error generating share link:', error);
            // Fallback for pages - use existing uniqueLinkId if available
            if (item.type === 'page' && item.uniqueLinkId) {
              textToCopy = `Hi there,

Here's the link to view:
https://integration.3sigmacrm.com/page/${item.uniqueLinkId}`;
            } else {
              throw error;
            }
          }
          break;

        default:
          throw new Error('Copy not supported for this content type');
      }

      // Copy to clipboard
      await navigator.clipboard.writeText(textToCopy);
      toast.success('Copied to clipboard!');
      Logger.log('Successfully copied content:', item.type);

    } catch (error) {
      console.error('Error copying content:', error);
      toast.error('Failed to copy content');
    } finally {
      setCopyingStates(prev => ({ ...prev, [itemId]: false }));
    }
  }, []);

  // Download functionality for files
  const handleDownloadFiles = useCallback(async (item: ContentI) => {
    if (item.type !== 'file' || !item.files || item.files.length === 0) {
      toast.error('No files to download');
      return;
    }

    const itemId = item._id;
    setDownloadingStates(prev => ({ ...prev, [itemId]: true }));

    try {
      Logger.log('Starting file downloads for:', item._id);

      if (item.files.length === 1) {
        // Single file download
        const file = item.files[0];
        
        // Type guard to ensure file has required properties
        if (!file?.filePath || !file?.fileName) {
          toast.error('File information is incomplete');
          return;
        }
        
        Logger.log('Downloading single file:', file.fileName);
        
        const blob = await downloadContentFile(file.filePath, file.fileName);
        const downloadFile = contentBlobToFile(blob, file.fileName);
        
        // Create download link and trigger download
        const url = URL.createObjectURL(downloadFile);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success(`Downloaded: ${file.fileName}`);
      } else {
        // Multiple files download
        let successCount = 0;
        
        for (const file of item.files) {
          try {
            // Type guard to ensure file has required properties
            if (!file?.filePath || !file?.fileName) {
              Logger.log('Skipping file with incomplete information:', file);
              continue;
            }
            
            Logger.log('Downloading file:', file.fileName);
            
            const blob = await downloadContentFile(file.filePath, file.fileName);
            const downloadFile = contentBlobToFile(blob, file.fileName);
            
            // Create download link and trigger download
            const url = URL.createObjectURL(downloadFile);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            
            successCount++;
            
            // Small delay between downloads to prevent browser blocking
            await new Promise(resolve => setTimeout(resolve, 500));
            
          } catch (error) {
            console.error(`Failed to download ${file?.fileName || 'unknown file'}:`, error);
          }
        }
        
        toast.success(`Downloaded ${successCount} of ${item.files.length} files`);
      }

    } catch (error) {
      console.error('Error downloading files:', error);
      toast.error('Failed to download files');
    } finally {
      setDownloadingStates(prev => ({ ...prev, [itemId]: false }));
    }
  }, []);

  // Memoized row click handler to maintain referential stability
  const handleRowClick = useCallback((item: ContentI, event: React.MouseEvent) => {
    // Prevent card click when action buttons are clicked
    if ((event.target as HTMLElement).closest('button')) {
      return;
    }

    // Set activeId first
    setActiveId(item._id);

    try {
      switch (activeTab) {
        case "message":
          setMessageData({
            title: item.details?.title || '',
            tags: item.details?.tags || ["no_tag"],
            message: item.details?.message || ''
          });
          setOpenMessageBox(true);
          break;
        
        case "page": {
          Logger.log('ContentGrid - Page clicked, raw data:', item);
          
          const safePageData = {
            title: item.details?.title || '',
            tags: item.details?.tags || ['no_tag'],
            description: item.details?.description || '',
            videoUrl: item.details?.videoUrl || '',
            location: item.details?.location || {},
            address: item.details?.address || '',
            files: Array.isArray(item.files) ? [...item.files] : [],
            uniqueLinkId: item.uniqueLinkId || '',
            _id: item._id,
            organization: item.organization,
            type: item.type,
            viewCount: item.viewCount || 0
          };
          
          Logger.log('ContentGrid - Setting page data with files:', safePageData.files);
          setPageData(safePageData);
          setOpenPageBox(true);
          break;
        }
        
        case "file":
          setOpenFileBox(true);
          break;
        
        case "email_template":
          if (item.details) {
            setEmailData({
              title: item.details.title || '',
              tags: item.details.tags || ["no_tag"],
              subject: item.details.subject || '',
              message: item.details.message || '',
              html: item.details.html || ''
            });
          }
          setOpenEmailBox(true);
          break;

        case "digital_card":
          Logger.log('ContentGrid - Digital card clicked:', item);
          // Digital card click will be handled differently - show viewer instead
          if (setDigitalCardData && setOpenDigitalCardBox) {
            setDigitalCardData({
              title: item.details?.title || '',
              tags: item.details?.tags || ['no_tag'],
              name: (item.details as any)?.name || '',
              email: (item.details as any)?.email || '',
              phone: (item.details as any)?.phone || '',
              whatsapp: (item.details as any)?.whatsapp || '',
              designation: (item.details as any)?.designation || '',
              companyName: (item.details as any)?.companyName || '',
              companyAddress: (item.details as any)?.companyAddress || '',
              briefDescription: (item.details as any)?.briefDescription || '',
              website: (item.details as any)?.website || '',
              facebook: (item.details as any)?.facebook || '',
              instagram: (item.details as any)?.instagram || '',
              linkedIn: (item.details as any)?.linkedIn || '',
              googleCalendar: (item.details as any)?.googleCalendar || '',
              cardUrl: (item.details as any)?.cardUrl || '',
              uniqueLinkId: item.uniqueLinkId || '',
              _id: item._id,
              organization: item.organization,
              type: item.type,
              viewCount: item.viewCount || 0
            });
            // Don't open form box, will be handled by parent component
          }
          break;

        default:
          Logger.warn(`[ContentGrid] Unhandled tab type: ${activeTab}`);
      }
    } catch (error) {
      console.error('[ContentGrid] Error in card click handler', error);
    }
  }, [
    activeTab,
    setActiveId,
    setMessageData,
    setOpenMessageBox,
    setPageData,
    setOpenPageBox,
    setOpenFileBox,
    setOpenEmailBox,
    setEmailData,
    setDigitalCardData,
    setOpenDigitalCardBox
  ]);

  // Memoize filtered and transformed data
  const processedData = useMemo(() => {
    const processed: ProcessedContentItem[] = filterData.map((item: ContentI) => ({
      ...item,
      truncatedTitle: truncateText(item.details?.title || '', 60),
      truncatedTag: truncateText(item.details?.tags?.[0] || 'no tag', 20),
      truncatedDescription: truncateText(
        activeTab === 'email_template' 
          ? item.details?.subject || ''
          : item.details?.description || item.details?.message || '', 
        120
      )
    }));

    return processed;
  }, [filterData, activeTab, truncateText]);

  // Render action buttons based on content type
  const renderActionButtons = useCallback((item: ContentI) => {
    const itemId = item._id;
    const isCopying = copyingStates[itemId];
    const isDownloading = downloadingStates[itemId];

    return (
      <ActionButtons>
        {/* Copy button for message, page, and file */}
        {(item.type === 'message' || item.type === 'page' || item.type === 'file') && (
          <ActionButton
            variant="secondary"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              handleCopyContent(item);
            }}
            disabled={isCopying}
            title="Copy content"
          >
            {isCopying ? <LoadingSpinner /> : <Copy />}
          </ActionButton>
        )}

        {/* Download button for files only */}
        {item.type === 'file' && (
          <ActionButton
            variant="primary"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              handleDownloadFiles(item);
            }}
            disabled={isDownloading}
            title="Download files"
          >
            {isDownloading ? <LoadingSpinner /> : <Download />}
          </ActionButton>
        )}
      </ActionButtons>
    );
  }, [copyingStates, downloadingStates, handleCopyContent, handleDownloadFiles]);

  return (
    <CardGrid>
      {processedData.map((item) => (
        <Card key={item._id} onClick={(e: React.MouseEvent<HTMLDivElement>) => handleRowClick(item, e)}>
          <CardHeader>
            <IconWrapper>
              <ContentTypeIcon size={20} />
            </IconWrapper>
            <CardTitle>{item.truncatedTitle}</CardTitle>
          </CardHeader>
          
          <CardBody>
            <CardDescription>
              {item.truncatedDescription}
            </CardDescription>
            
            <TagsContainer>
              {item.details?.tags?.map((tag, index) => (
                <ContentTag key={`${item._id}-tag-${index}`}>
                  {tag.replace(/_/g, ' ')}
                </ContentTag>
              ))}
              {(!item.details?.tags || item.details?.tags.length === 0) && (
                <ContentTag>no tag</ContentTag>
              )}
            </TagsContainer>
          </CardBody>
          
          <CardFooter>
            <ViewCount>
              <Eye size={14} />
              {item.viewCount || 0} views
            </ViewCount>
            
            {renderActionButtons(item)}
          </CardFooter>
        </Card>
      ))}
      
      {processedData.length === 0 && (
        <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
          {searchQuery ? (
            // Empty state for search with no results
            <>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
              <h3 style={{ margin: '0 0 8px 0', color: '#374151', fontFamily: 'Gilroy', fontSize: '20px', fontWeight: 600 }}>
                No results found
              </h3>
              <p style={{ margin: 0, fontFamily: 'Gilroy', fontSize: '14px', color: '#64748b' }}>
                Try adjusting your search or filters
              </p>
            </>
          ) : (
            // Empty state for no content at all
            <>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📂</div>
              <h3 style={{ margin: '0 0 8px 0', color: '#374151', fontFamily: 'Gilroy', fontSize: '20px', fontWeight: 600 }}>
                No content found
              </h3>
              <p style={{ margin: 0, fontFamily: 'Gilroy', fontSize: '14px', color: '#64748b' }}>
                Create some content to get started
              </p>
            </>
          )}
        </div>
      )}
    </CardGrid>
  );
});

export default ContentGrid;